/**
 * useSidebarMode — desktop sidebar display mode with persistence.
 *
 * Modes:
 *   'open'   — full 240px rail (default)
 *   'icons'  — 76px rail, icon + tiny label per item
 *   'closed' — hidden entirely
 *
 * The mode is stored in localStorage and broadcast through a window event
 * so every consumer (AppShell, Topbar toggle, Settings page) stays in sync
 * within the same tab.
 */

import { useCallback, useEffect, useState } from 'react';

const KEY = 'orin-sidebar-mode';
const EVENT = 'orin:sidebar-mode';
export const SIDEBAR_MODES = ['open', 'icons', 'closed'];

export function getStoredSidebarMode() {
  try {
    const v = localStorage.getItem(KEY);
    return SIDEBAR_MODES.includes(v) ? v : 'open';
  } catch {
    return 'open';
  }
}

export function setStoredSidebarMode(mode) {
  const safe = SIDEBAR_MODES.includes(mode) ? mode : 'open';
  try {
    localStorage.setItem(KEY, safe);
  } catch {
    /* storage blocked — mode still applies for this render */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: safe }));
}

export function useSidebarMode() {
  const [mode, setMode] = useState(getStoredSidebarMode);

  useEffect(() => {
    const handler = (e) => {
      setMode(SIDEBAR_MODES.includes(e.detail) ? e.detail : 'open');
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  const set = useCallback((m) => setStoredSidebarMode(m), []);

  const cycle = useCallback(() => {
    const current = getStoredSidebarMode();
    const next = SIDEBAR_MODES[(SIDEBAR_MODES.indexOf(current) + 1) % SIDEBAR_MODES.length];
    setStoredSidebarMode(next);
  }, []);

  return { mode, set, cycle };
}
